import React, {
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback
} from 'react';
import {
  Badge,
  Box,
  Container,
  Fab
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import CloseIcon from 'remixicon-react/CloseFillIcon';
import HelpIcon from 'remixicon-react/QuestionnaireFillIcon';
import { AppContext } from './AppContext';
import ChatFrame from './ChatFrame';
import { useIframe, useWidgetMinimizedSignal } from './FrameUtils';
import ProactiveTeaser from './ProactiveTeaser';
import { useTheme } from '../theme';

// The unread count is part of the chat button's accessible name (the visual
// badge is aria-hidden, so screen readers hear it exactly once).
const getChatButtonLabel = (showChat: boolean, unreadCount: number) => {
  if (showChat) {
    return 'Close chat';
  }
  if (unreadCount === 0) {
    return 'Open chat';
  }
  const noun = unreadCount === 1 ? 'unread message' : 'unread messages';
  return `Open chat, ${unreadCount} ${noun}`;
};

function App() {
  const { state, dispatch } = useContext(AppContext);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    window.matchMedia('(max-width: 768px)').matches
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);

  const { theme, config } = useTheme();
  const postMessageToFrame = useIframe(dispatch);

  // ------------------------------------------------------------------
  // Proactive teaser: shown when the chat is minimized and the iframe has
  // forwarded an unread proactive message. Clicking opens the chat and asks
  // the iframe to scroll to it; dismissing delegates the real dismiss flow
  // back to the iframe (which owns the endpoint, analytics, and state).
  // ------------------------------------------------------------------
  const showTeaser =
    !state.showChat &&
    config?.ProactiveShowWhenMinimized !== false &&
    !!state.proactiveUnread &&
    !!state.proactiveTeaserText;

  // The unread count renders on the chat toggle button, not the teaser: the
  // teaser previews what the message says, the badge counts what is unread.
  // Opening the chat or dismissing the teaser clears it (both acknowledge).
  const showUnreadBadge = !state.showChat && config?.ProactiveShowWhenMinimized !== false;
  const unreadCount = showUnreadBadge ? state.proactiveUnreadCount : 0;

  const openProactive = () => {
    dispatch({ type: 'setShowChat', showChat: true });
    dispatch({ type: 'setHasOpened', hasOpened: true });
    dispatch({ type: 'clearProactiveTeaser' });
    postMessageToFrame({
      isEdulla: true,
      action: 'openProactive',
      data: { proactiveMessageId: state.proactiveMessageId },
    });
  };

  const dismissProactiveTeaser = () => {
    postMessageToFrame({
      isEdulla: true,
      action: 'dismissProactive',
      data: { proactiveMessageId: state.proactiveMessageId },
    });
    dispatch({ type: 'clearProactiveTeaser' });
  };

  // Implicit-dismiss signal (ADR #55): notify the iframe when the widget is
  // collapsed so it can decide whether a close-right-after-a-nudge is a dismissal.
  useWidgetMinimizedSignal(state.showChat, postMessageToFrame);

  const updateToggleOffset = useCallback(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const toggle = toggleRef.current;

    if (!toggle) {
      container.style.removeProperty('--toggle-offset');
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const toggleRect = toggle.getBoundingClientRect();
    const toggleButton = toggle.querySelector('button');
    const targetRect = toggleButton?.getBoundingClientRect() ?? toggleRect;
    const offset = Math.max(0, containerRect.bottom - targetRect.top);

    container.style.setProperty('--toggle-offset', `${Math.ceil(offset)}px`);
  }, []);

  // ------------------------------------------------------------------
  // Automatically show the prompt after 5s IF the user hasn't interacted.
  // We use a useEffect to avoid multiple timeouts and race conditions.
  // ------------------------------------------------------------------
  const shouldShowPromptForDevice = () => {
    if (isMobile) {
      return config?.PromptBubbleShowOnMobile !== false;
    }
    return config?.PromptBubbleShowOnDesktop !== false;
  };

  useEffect(() => {
    // Skip if user has permanently dismissed the prompt
    if (state.hasShownPrompt) {
      return;
    }

    // Skip if device config disables prompt
    if (!shouldShowPromptForDevice()) {
      return;
    }

    // Only set the timeout if all these conditions are true
    // meaning the user hasn't opened the chat or entered info, etc.
    if (!showPrompt && !state.showChat && !state.enteredInformation && !state.hasOpened) {
      const timerId = setTimeout(() => {
        // Double-check states before actually showing the prompt
        if (!showPrompt && !state.showChat && !state.enteredInformation && !state.hasOpened) {
          setShowPrompt(true);
        }
      }, 5000);

      // Cleanup if component unmounts or any dependency changes
      return () => clearTimeout(timerId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPrompt, state.showChat, state.enteredInformation, state.hasOpened, state.hasShownPrompt, isMobile, config]);

  // ------------------------------------------------------------------
  // Automatically open chat if OpenByDefault is enabled in config
  // ------------------------------------------------------------------
  useEffect(() => {
    if (config?.OpenByDefault && !state.hasOpened) {
      dispatch({ type: 'setShowChat', showChat: true });
      dispatch({ type: 'setHasOpened', hasOpened: true });
    }
  }, [config?.OpenByDefault, state.hasOpened, dispatch]);

  // ------------------------------------------------------------------
  // Listen for device size changes (mobile vs desktop)
  // ------------------------------------------------------------------
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // ------------------------------------------------------------------
  // Toggles the chat window open/closed
  // ------------------------------------------------------------------
  const toggleChat = () => {
    setShowPrompt(false);

    // If chat is closed, open it; if open, close it.
    dispatch({ type: 'setShowChat', showChat: !state.showChat });

    // Mark as opened so the prompt won't appear again
    dispatch({ type: 'setHasOpened', hasOpened: true });
  };

  // ------------------------------------------------------------------
  // Dismiss the prompt bubble permanently
  // ------------------------------------------------------------------
  const dismissPrompt = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowPrompt(false);
    dispatch({ type: 'setHasShownPrompt', hasShownPrompt: true });
  };

  useEffect(() => {
    if (!state.showChat) {
      return;
    }

    const timeout = window.setTimeout(() => {
      document.getElementById('edullaChatWidgetFrame')?.focus();
    }, 0);

    return () => window.clearTimeout(timeout);
  }, [state.showChat]);


  useLayoutEffect(() => {
    let frameId = requestAnimationFrame(updateToggleOffset);

    const scheduleUpdate = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(updateToggleOffset);
    };

    window.addEventListener('resize', scheduleUpdate);

    let resizeObserver: ResizeObserver | undefined;

    const observeToggleElements = () => {
      if (!resizeObserver) {
        return;
      }

      const toggleElement = toggleRef.current;
      if (!toggleElement) {
        return;
      }

      resizeObserver.observe(toggleElement);
      const toggleButton = toggleElement.querySelector('button');
      if (toggleButton) {
        resizeObserver.observe(toggleButton);
      }
    };

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }
      observeToggleElements();
    }

    let mutationObserver: MutationObserver | undefined;
    if (typeof MutationObserver !== 'undefined' && containerRef.current) {
      mutationObserver = new MutationObserver(() => {
        observeToggleElements();
        scheduleUpdate();
      });
      mutationObserver.observe(containerRef.current, {
        attributes: true,
        childList: true,
        subtree: true
      });
    }

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', scheduleUpdate);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [updateToggleOffset]);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  if (!theme) {
    return null;
  }

  const chatButtonLabel = getChatButtonLabel(state.showChat, unreadCount);

  return (
    <ThemeProvider theme={theme}>
      {/* StrictMode is optional; it's great for dev but often removed in prod */}
      <React.StrictMode>
        <Container
          component="aside"
          role="complementary"
          aria-label="Site assistance chat"
          ref={containerRef}
          sx={{
            p: '0 !important',
            position: 'fixed',
            bottom: 25,
            right: 25,
            width: 'auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
            gap: 0,
            maxHeight: '95vh',
            height: state.showChat ? '95vh' : 'auto',
            background: 'none !important',
            zIndex: 999999,
            pointerEvents: 'none',
          }}
        >
          {/* The actual iframe-based chat window */}
          <ChatFrame />

          {/* The bubble prompt (appears if user hasn't interacted after 5s) */}
          <Box
            sx={{
              fontFamily: 'sans-serif',
              position: 'absolute',
              right: 0,
              bottom: 69,
              background: '#e8e8e8 !important',
              borderRadius: '6px',
              maxHeight: showPrompt ? '90px' : '0px',
              maxWidth: showPrompt ? '260px' : '0px',
              overflow: 'hidden',
              opacity: showPrompt ? 1 : 0,
              // Hidden state must use visibility (not just opacity) so the
              // collapsed bubble is removed from the accessibility tree and
              // excluded from contrast tooling. visibility is delayed on the
              // way out so the fade still plays before it disappears.
              visibility: showPrompt ? 'visible' : 'hidden',
              transitionProperty: 'max-height, max-width, opacity, padding, visibility',
              transitionDuration: '0.5s',
              transitionDelay: showPrompt ? '0s' : '0s, 0s, 0s, 0s, 0.5s',
              padding: showPrompt ? '10px 32px 10px 20px' : '0px',
              minWidth: '200px',
              fontSize: '16px',
              color: '#212529 !important',
              boxShadow:
                '0px 3px 5px -1px rgba(0,0,0,0.2),' +
                '0px 6px 10px 0px rgba(0,0,0,0.14),' +
                '0px 1px 18px 0px rgba(0,0,0,0.12)',
              pointerEvents: showPrompt ? 'auto' : 'none'
            }}
            role="status"
            aria-live="polite"
            aria-hidden={!showPrompt}
          >
            <Box
              component="button"
              onClick={dismissPrompt}
              aria-label="Dismiss greeting"
              sx={{
                position: 'absolute',
                top: '4px',
                right: '4px',
                width: '20px',
                height: '20px',
                padding: 0,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                '&:hover': {
                  background: 'rgba(0,0,0,0.1)',
                },
                '&:focus': {
                  outline: '2px solid #666',
                  outlineOffset: '1px',
                }
              }}
            >
              <CloseIcon size={14} />
            </Box>
            {config?.PromptBubbleMessage || "Hello! Is there anything I can help you with today?"}
          </Box>

          {/* Proactive teaser (shown when minimized with an unread proactive message) */}
          {showTeaser && (
            <ProactiveTeaser
              text={state.proactiveTeaserText}
              botName={config?.BotName}
              primaryColor={config?.PrimaryColor}
              onOpen={openProactive}
              onDismiss={dismissProactiveTeaser}
            />
          )}

          {/* The floating chat toggle button, badged with the unread proactive
              count. MUI gives the Fab zIndex.fab (1050) while the badge defaults
              to 1, which would paint the badge behind the button — the inline
              style lifts it one above. */}
          <Badge
            badgeContent={unreadCount}
            max={99}
            color="error"
            overlap="circular"
            componentsProps={{
              badge: { 'aria-hidden': true, style: { zIndex: theme.zIndex.fab + 1 } },
            }}
            sx={{ pointerEvents: 'auto' }}
          >
            <Fab
              ref={toggleRef}
              onClick={toggleChat}
              aria-label={chatButtonLabel}
              aria-expanded={state.showChat}
              color="primary"
              sx={{ pointerEvents: 'auto' }}
            >
              {state.showChat ? <CloseIcon size={24} /> : <HelpIcon size={24} />}
            </Fab>
          </Badge>
        </Container>
      </React.StrictMode>
    </ThemeProvider>
  );
}

export default App;
